import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import {
  ApiTypeEnum,
  ApiTypesEnum, AttachmentUploadedOutDTO,
  CarrierOrGroupEnum,
  CurrencyLockTypeEnum,
  DachserDeliveryTermEnum,
  DachserProductTypeEnum,
  DhlProductTypeEnum,
  DistributionInDTO,
  DistributionTypeEnum, EntityTypeEnum,
  ExpeditionDetailV4OutDTO,
  FeaturesEnum,
  FindCarrierTypeEnum,
  GetPreviousDistributionInnerResponseOutDTO,
  OpeningModeEnum,
  PermissionTypesEnum,
  ResponseDeadlineModeEnum,
  SchenkerAddonsTypeEnum,
  SchenkerProductTypeEnum,
  SelectedItemTypeEnum,
} from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import useForm from '@hooks/useForm/useForm';
import * as yup from 'yup';
import { ladenFactorValueValidation } from '@utils/expeditionUtils.ts';
import { createLogger } from '@utils/logger';
import {
  apiPragueFromDayjsLocal,
  apiUtcFromDayjs,
  dayjsFromDayAndTimeStrings,
} from '@utils/dateWithTz';
import dayjs from 'dayjs';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import { AiCarrierAutoSelectionDto } from '@components/ai/AiCarrierAutoSelection.tsx';
import { timeslotLocalDay } from '@components/RingilComponents/FedexPickupForm.tsx';
import CarrierDistributionSelector, {
  CarrierDistributionSelectorData,
  DistributionType,
  resolveResponseDeadline,
} from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import { fromContractedCarrierOutDTO, fromPriceTableDTO } from '@components/ringil3/Features/Contracted/types.ts';
import { contractedCarriersValidation } from '@app/pages/expedition/create/validation.ts';
import { muCodesOf } from '@app/pages/expedition/create/contractedUnits.ts';
import { mapContractedDistributionToRequest } from '@app/pages/expedition/create/mappers/formToSaveRequest.ts';
import { mapLineDistribution } from '@app/pages/expedition/create/mappers/lineToForm.ts';


interface ComponentProps {
  onContinue: () => void;
  handlingUnitCount?: number;
  ladenFactorEligible: boolean;
  aiCarrierAutoSelectionDto?: AiCarrierAutoSelectionDto;
  expedition?: ExpeditionDetailV4OutDTO;
  /**
   * Carriers to start with, e.g. the ones an AI-dispatcher rule recommended. Everything else —
   * price, deadline, distribution type — is still the user's to enter.
   */
  preselectedCarrierIds?: string[];
}

interface FormData {
  distribution: CarrierDistributionSelectorData;
}

const log = createLogger('requestCarrierSidebar');

export const useRequestCarrierSidebar = () => {
  const [visible, setVisible] = useState(false);

  const RequestCarrierSidebar: React.FC<ComponentProps> = ({
                                                             onContinue,
                                                             handlingUnitCount,
                                                             ladenFactorEligible,
                                                             aiCarrierAutoSelectionDto,
                                                             expedition,
                                                             preselectedCarrierIds,
                                                           }) => {
    const { tr } = useTranslationLgs();
    const { loggedUser, hasFeature } = useContext(AuthenticatedUserContext);
    const {
      cuCreateDistributionForExpedition,
      createDistribution,
      getDistributionSettings,
      getPreviousDistribution,
      getTmplV2Detail,
      listDocumentsForEntity,
    } = useContext(ApiContext);
    // RD-2978: a shipment from a Linka is distributed again only to its contracted carriers
    const fromLine = !!expedition?.tmplV2Id;
    const timezoneId = loggedUser?.defaultTimezoneId ?? 'Europe/Prague';
    // handling units per manipulation unit - what the contracted prices are applied to
    const contractedUnitsByMu = (expedition?.locations ?? [])
      .flatMap(loc => loc.loadedOrders ?? [])
      .flatMap(order => order.items ?? [])
      .reduce<Record<string, number>>((units, item) => {
        const code = item.cargoTemplate?.code;
        if (code) units[code] = (units[code] ?? 0) + (item.amount ?? 0);
        return units;
      }, {});
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
    const [distributionHours, setDistributionHours] = useState<number>(8);
    const [loading, setLoading] = useState<boolean>(false);

    const [originalDistribution, setOriginalDistribution] = useState<CarrierDistributionSelectorData>(null);
    const [attachments, setAttachments] = useState<AttachmentUploadedOutDTO[]>([]);

    const originalDistributionAllowed = useSecuredFlag('request-original-distribution', {
      kind: 'visibility',
      description: 'Load original distribution in request-carrier sidebar',
      permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE_DISTRIBUTION_RW],
    }).granted;

    useEffect(() => {
      if (!visible) return;
      listDocumentsForEntity(EntityTypeEnum.EXPEDITION, expedition.id, d => {
        setAttachments(d);
      });
    }, [visible]);

    const getOriginalDistribution = (distribution?: GetPreviousDistributionInnerResponseOutDTO): CarrierDistributionSelectorData | null => {
      if (!distribution) return null;

      const fixedOrSpot = distribution.spot || distribution.fixed;
      const price = fixedOrSpot?.price || distribution.assignedPrice;

      const selectedItems = fixedOrSpot?.selectedItems?.map(item => ({
        id: item.id,
        userIds: item.userId ? [item.userId] : null,
        apiTypes: item.apiType ? [item.apiType as unknown as ApiTypesEnum] : null,
        carrierType: item.selectedItemType === SelectedItemTypeEnum.API
          ? FindCarrierTypeEnum.API
          : item.selectedItemType === SelectedItemTypeEnum.GROUP
            ? FindCarrierTypeEnum.GROUP
            : item.selectedItemType === SelectedItemTypeEnum.CARRIER && item.userId && item.userId.length > 0
              ? FindCarrierTypeEnum.CARRIER_USER
              : FindCarrierTypeEnum.CARRIER,
        _dachserProductTypeCode: fixedOrSpot.dachserProductType,
        _dachserDeliveryTermCode: fixedOrSpot.dachserDeliveryTerm,
        _schenkerProductTypeCode: fixedOrSpot.schenkerProductType,
        _schenkerAddonsTypeCodes: fixedOrSpot.schenkerAddonsType,
      })) ||
        // A CONTRACTED previous distribution cannot be re-sent from here (prices are per contract);
        // its carriers are offered for a spot request instead.
        (distribution.contractedCarriers?.length
          ? distribution.contractedCarriers.map(carrier => ({ id: carrier.carrierId, name: carrier.carrierName, carrierType: FindCarrierTypeEnum.CARRIER }))
          : null) ||
        (distribution.assignedCarrierId ? [{ id: distribution.assignedCarrierId }] : []);

      return {
        distributionType: distribution.spot || distribution.contractedCarriers?.length ? DistributionType.SPOT : DistributionType.FIXED,
        price,
        selectedItems,
        responseDeadlineMode: 'relative' as const,
        responseDeadlineOffsetValue: distributionHours,
        responseDeadlineOffsetUnit: 'hours' as const,
        responseDeadlineTimezone: loggedUser.defaultTimezoneId,
        notifyAssignedCarrier: false,
        currencyLockType: distribution.currencyLockType || CurrencyLockTypeEnum.NONE,
        ladenFactor: distribution.ladenFactor > 0,
        ladenFactorValue: distribution.ladenFactor,
        allowPartialDelivery: distribution.allowPartialDelivery,
      };
    };

    /** The previous CONTRACTED distribution of a Linka shipment again: its price table, plan and carriers. */
    const contractedFromPrevious = (distribution?: GetPreviousDistributionInnerResponseOutDTO): CarrierDistributionSelectorData | null => {
      if (!distribution?.contractedPriceTable) return null;
      const table = fromPriceTableDTO(distribution.contractedPriceTable);
      return {
        ...initDistribution(),
        distributionType: DistributionType.CONTRACTED,
        selectedItems: [],
        price: { price: 0, currency: table.currency ?? loggedUser?.defaultCurrencyIsoCode },
        currencyLockType: CurrencyLockTypeEnum.NONE,
        contractedPriceTable: table,
        contractedCarriers: (distribution.contractedCarriers ?? []).map(c => fromContractedCarrierOutDTO(c, table)),
        ladenFactor: distribution.ladenFactor > 0 ? true : undefined,
        ladenFactorValue: distribution.ladenFactor > 0 ? distribution.ladenFactor : undefined,
        allowPartialDelivery: distribution.allowPartialDelivery,
      };
    };

    // Linka shipment: start from its last contracted distribution, without one (never distributed) from the Linka itself
    useEffect(() => {
      if (!visible || !fromLine) return;
      const fromLinka = () =>
        getTmplV2Detail(expedition.tmplV2Id, detail => {
          const distribution = detail
            ? mapLineDistribution(detail, {
                defaultCurrencyIsoCode: loggedUser?.defaultCurrencyIsoCode,
                defaultTimezoneId: timezoneId,
                deadlineMode: 'relative',
                effectiveHours: distributionHours,
                editDistributionAllowed: true,
                ladenFactorEnabled: hasFeature([FeaturesEnum.LADEN_FACTOR]),
                contractedEnabled: hasFeature([FeaturesEnum.CONTRACTED_CARRIERS]),
                locationDataByLocationId: new Map(),
              })
            : undefined;
          if (distribution) form.setFieldValue(form.names.distribution, distribution);
        });
      getPreviousDistribution(expedition.id, data => {
        const previous = contractedFromPrevious(data?.previousDistribution);
        if (previous) form.setFieldValue(form.names.distribution, previous);
        else fromLinka();
      });
    }, [visible, fromLine, expedition.id]);

    useEffect(() => {
      if (visible && expedition && originalDistributionAllowed && !fromLine) {
        getPreviousDistribution(
          expedition.id,
          (data) => {
            if (data && data.previousDistribution) {
              setOriginalDistribution(getOriginalDistribution(data.previousDistribution));
            }
          },
        );
      }
    }, [expedition.id, distributionHours, visible, originalDistributionAllowed]);

    const currencyLockEnabled = useSecuredFlag('request-currency-lock', {
      kind: 'feature',
      description: 'Currency lock in request-carrier sidebar',
      features: [FeaturesEnum.CURRENCY_LOCK],
    }).granted;

    const initDistribution = (): CarrierDistributionSelectorData => {
      // Only prefill the laden factor value with the expedition default when it is an even divisor of the
      // handling unit count (i.e. it would be a computed option). Otherwise leave it for the user to pick.
      const defaultLadenFactorIsComputed =
        !!handlingUnitCount && !!expedition?.defaultLadenFactor && handlingUnitCount % expedition.defaultLadenFactor === 0;

      return {
        distributionType: fromLine ? DistributionType.CONTRACTED : DistributionType.SPOT,
        // Carriers handed in from outside (an AI-dispatcher recommendation) start selected. Set
        // here as well as in the effect below because the hook rebuilds this component on every
        // parent render, and each rebuild is a fresh mount that re-runs these initial values.
        selectedItems: (preselectedCarrierIds ?? []).map(id => ({ id, carrierType: FindCarrierTypeEnum.CARRIER })),
        responseDeadlineMode: 'relative',
        responseDeadlineOffsetValue: distributionHours,
        responseDeadlineOffsetUnit: 'hours',
        responseDeadlineTimezone: loggedUser.defaultTimezoneId,
        price: { currency: loggedUser?.defaultCurrencyIsoCode, price: 0 },
        notifyAssignedCarrier: false,
        currencyLockType: currencyLockEnabled ? CurrencyLockTypeEnum.ONLY_SELECTED : CurrencyLockTypeEnum.NONE,
        ladenFactor: ladenFactorEligible ? true : null,
        ladenFactorValue: ladenFactorEligible && defaultLadenFactorIsComputed ? expedition.defaultLadenFactor : null,
        allowPartialDelivery: ladenFactorEligible ? true : null,
      };
    };

    const validationRules = yup.object().shape({
      distribution: yup.object().shape({
        distributionType: yup.string(),
        ladenFactorValue: ladenFactorValueValidation(
          {
            shouldBeNumber: tr('RequestCarrierSidebar.ladenFactorShouldBeNumber', 'Laden factor should be a number'),
            required: tr('RequestCarrierSidebar.mandatoryLadenFactor', 'Laden factor is required'),
          },
          hasFeature([FeaturesEnum.LADEN_FACTOR]),
        ),
        price: yup.object().when('distributionType', {
          is: (type: string) => !!type, // pokud je jakýkoliv typ distribuce
          then: yup.object().shape({
            price: yup.number()
              .typeError(tr('RequestCarrierSidebar.priceShouldBeNumber', 'Price should be number'))
              .transform((value) => (isNaN(value) ? undefined : value))
              .nullable()
              .required(tr('RequestCarrierSidebar.mandatoryPrice', 'Price is required')),
            currency: yup.string().required(tr('CreateExpedition.mandatoryCurrency', 'Currency is required')),
          }).required(tr('RequestCarrierSidebar.mandatoryPrice', 'Price is required')),
          otherwise: yup.object().nullable(),
        }),
        contractedCarriers: contractedCarriersValidation(tr, muCodesOf(contractedUnitsByMu), timezoneId),
        selectedItems: yup.array().when('distributionType', {
          is: (type: string) => type === 'FIXED' || type === 'SPOT' || type === 'ASSIGN',
          then: yup.array()
            .min(1, tr('RequestCarrierSidebar.mandatoryCarriers', 'Please select at least one carrier'))
            .required(tr('RequestCarrierSidebar.mandatoryCarriers', 'Please select at least one carrier'))
            // FedEx Express Freight needs the booking confirmation number from FedEx Freight Customer Service
            .test(
              'fedex-freight-booking',
              tr('FedexProductTerms.bookingConfirmationRequired', 'FedEx booking confirmation number is required for freight services'),
              items => !items?.some(item => item._fedexProductTypeCode?.includes('_FREIGHT') && !item._fedexBookingConfirmationNumber?.trim()),
            )
            // several FedEx accounts allowed for the route - the user has to pick one
            .test(
              'fedex-account',
              tr('FedexProductTerms.accountRequired', 'Select the FedEx account the shipment goes on'),
              items => !items?.some(item => item._fedexAccountRequired && !item._fedexAccountId),
            ),
          otherwise: yup.array().nullable(),
        }),
        responseDeadlineOffsetValue: yup.number().when(['distributionType', 'responseDeadlineMode'], {
          is: (type: string, mode: string) => (type === 'FIXED' || type === 'SPOT' || type === 'CONTRACTED') && mode !== 'absolute',
          then: yup.number()
            .typeError(tr('InputDeadline.required', 'Offer deadline is required'))
            .min(1, tr('InputDeadline.required', 'Offer deadline is required'))
            .required(tr('InputDeadline.required', 'Offer deadline is required')),
          otherwise: yup.number().nullable(),
        }),
        responseDeadlineLocalTime: yup.mixed().when(['distributionType', 'responseDeadlineMode'], {
          is: (type: string, mode: string) => (type === 'FIXED' || type === 'SPOT' || type === 'CONTRACTED') && mode === 'absolute',
          then: yup.mixed().required(tr('InputDeadline.required', 'Offer deadline is required')),
          otherwise: yup.mixed().nullable(),
        }),
      }).nullable(),
    });

    const form = useForm<FormData>(
      validationRules,
      {
        distribution: initDistribution(),
      },
      d => onContinueHandler(d),
      false,
      false,
    );

    useEffect(() => {
      log.debug('Form validation errors:', form.validationErrors);
    }, [form?.validationErrors]);

    useEffect(() => {
      if (!visible) return;
      getDistributionSettings(d => {
        const hours = d.defaultHoursInFuture;
        setDistributionHours(hours);
        form.setFieldValue('distribution.responseDeadlineOffsetValue', hours);

        const mode: 'relative' | 'absolute' =
          d.responseDeadlineMode === ResponseDeadlineModeEnum.ABSOLUTE ? 'absolute' : 'relative';
        form.setFieldValue('distribution.responseDeadlineMode', mode);
        if (mode === 'absolute') {
          form.setFieldValue('distribution.responseDeadlineLocalTime', dayjs().add(hours, 'hour'));
        }
      });
    }, [visible]);

    // Belt and braces with the initial value above: when the component happens to already be
    // mounted as the prop arrives, the initial values are long gone and only this applies them.
    useEffect(() => {
      log.debug('preselected carriers', { visible, preselectedCarrierIds });
      if (!visible || !preselectedCarrierIds?.length) return;
      form.setFieldValue(
        'distribution.selectedItems',
        preselectedCarrierIds.map(id => ({ id, carrierType: FindCarrierTypeEnum.CARRIER })),
      );
    }, [visible, preselectedCarrierIds]);

    if (!visible) return null;

    const mapToDistributionInDTO = (distribution: CarrierDistributionSelectorData): DistributionInDTO => {

      const dachserItem = distribution?.selectedItems?.find(item => item.apiTypes?.includes(ApiTypesEnum.DACHSER));
      const schenkerItem = distribution?.selectedItems?.find(item => item.apiTypes?.includes(ApiTypesEnum.SCHENKER));
      const dhlItem = distribution?.selectedItems?.find(item => item.apiTypes?.includes(ApiTypesEnum.DHL));
      const resolvedDeadline = resolveResponseDeadline(distribution, loggedUser.defaultTimezoneId);

      const pickupTz = expedition?.locations?.[0]?.timezoneId;
      const pickupDate = dhlItem?._dhlPickupCapabilities?.localCutoffDateAndTime?.substring(0, 10);
      const dhlAdditional = dhlItem?._dhlCourierRequested
        ? {
            dhlCourierRequested: true,
            dhlPickupReadyTime: pickupDate && dhlItem._dhlPickupReadyTime && pickupTz
              ? dayjsFromDayAndTimeStrings(pickupDate, `${dhlItem._dhlPickupReadyTime}:00`, pickupTz)?.toISOString()
              : undefined,
            dhlPickupCloseTime: dhlItem._dhlPickupCloseTime ? `${dhlItem._dhlPickupCloseTime}:00` : undefined,
          }
        : {};

      const trimmedPayer = dhlItem?._dhlPayerAccountNumber?.trim() ?? '';
      const dhlPayerFields = dhlItem?._dhlDifferentPayer
        ? {
            dhlPayerAccountNumber: trimmedPayer,
            dhlDutiesTaxesAccountNumber: dhlItem._dhlApplyToDutiesTaxes ? trimmedPayer : undefined,
          }
        : {};

      const fedexItem = distribution?.selectedItems?.find(item => item.apiTypes?.includes(ApiTypesEnum.FEDEX));
      // pickup date chosen from the open loading days FedEx offered; the first open day
      // of the loading location's timeslots when the availability has not answered yet
      const fedexFirstOpenDay = expedition?.locations?.[0]?.deliverySlot?.dayWithInterval
        ?.find(day => day.openingMode !== OpeningModeEnum.CLOSED);
      const fedexPickupDate = fedexItem?._fedexPickupDate ?? timeslotLocalDay(fedexFirstOpenDay);
      const fedexAdditional = fedexItem?._fedexCourierRequested
        ? {
            fedexCourierRequested: true,
            fedexPickupReadyTime: fedexPickupDate && fedexItem._fedexPickupReadyTime && pickupTz
              ? dayjsFromDayAndTimeStrings(fedexPickupDate, `${fedexItem._fedexPickupReadyTime}:00`, pickupTz)?.toISOString()
              : undefined,
            fedexPickupCloseTime: fedexItem._fedexPickupCloseTime ? `${fedexItem._fedexPickupCloseTime}:00` : undefined,
          }
        : {};

      return {
        distributionType: distribution.distributionType === DistributionType.SPOT ? DistributionTypeEnum.SPOT : DistributionTypeEnum.FIXED_PRICE,
        price: distribution.price?.price,
        currency: distribution.price?.currency,
        selectedItems: distribution.selectedItems.map(carrier => {
          let selectedItemType: SelectedItemTypeEnum;
          switch (carrier.carrierType) {
            case 'GROUP':
              selectedItemType = SelectedItemTypeEnum.GROUP;
              break;
            case FindCarrierTypeEnum.API:
              selectedItemType = SelectedItemTypeEnum.API;
              break;
            default:
              selectedItemType = SelectedItemTypeEnum.CARRIER;
          }
          return {
            id: carrier.id,
            carrierOrGroup: carrier.carrierType === 'CARRIER' || carrier.carrierType === 'CARRIER_USER'
              ? CarrierOrGroupEnum.CARRIER
              : CarrierOrGroupEnum.GROUP,
            selectedItemType: selectedItemType,
            apiType: carrier.apiTypes && carrier.apiTypes.length > 0 ? carrier.apiTypes[0] as unknown as ApiTypeEnum : null,
            userId: Array.isArray(carrier.userIds) && carrier.userIds.length > 0 ? carrier.userIds[0] : null,
          };
        }),
        validTill: apiPragueFromDayjsLocal(resolvedDeadline.localTime),
        utcEnd: apiUtcFromDayjs(resolvedDeadline.localTime),
        timezoneId: resolvedDeadline.timezone,
        currencyLockType: distribution.currencyLockType,
        allowPartialDelivery: distribution.ladenFactor ? distribution.allowPartialDelivery : null,
        ladenFactor: distribution.ladenFactor ? distribution.ladenFactorValue : null,
        dachserProductType: dachserItem?._dachserProductTypeCode as DachserProductTypeEnum,
        dachserDeliveryTerm: dachserItem?._dachserDeliveryTermCode as DachserDeliveryTermEnum,
        schenkerProductType: schenkerItem?._schenkerProductTypeCode as SchenkerProductTypeEnum,
        schenkerAddonsType : schenkerItem?._schenkerAddonsTypeCodes as SchenkerAddonsTypeEnum[],
        dhlProductType: dhlItem?._dhlProductTypeCode as DhlProductTypeEnum,
        ...dhlAdditional,
        ...dhlPayerFields,
        fedexProductType: fedexItem?._fedexProductTypeCode,
        ...fedexAdditional,
        fedexPayerType: fedexItem?._fedexPayerType,
        fedexPayerAccountNumber: fedexItem?._fedexPayerAccountNumber,
        fedexPayerCountry: fedexItem?._fedexPayerCountry,
        fedexDeclaredValue: fedexItem?._fedexDeclaredValue,
        fedexDeclaredValueCurrency: fedexItem?._fedexDeclaredValueCurrency,
        fedexSignatureOption: fedexItem?._fedexSignatureOption,
        fedexPackagingType: fedexItem?._fedexPackagingType,
        fedexBookingConfirmationNumber: fedexItem?._fedexBookingConfirmationNumber,
        fedexAccountId: fedexItem?._fedexAccountId,
      };
    };


    const onContinueHandler = (data: FormData) => {
      setLoading(true)
      const callbacks = {
        onValidationFailed: (errors: any) => {
          setLoading(false);
          setBeValidationErrors(errors);
        },
      };
      if (data.distribution.distributionType === DistributionType.CONTRACTED) {
        createDistribution(
          expedition.id,
          mapContractedDistributionToRequest(data.distribution, timezoneId),
          () => {
            setLoading(false);
            setVisible(false);
            onContinue();
          },
          null,
          callbacks,
        );
        return;
      }
      cuCreateDistributionForExpedition(
        expedition.customerId,
        expedition.applicationId,
        mapToDistributionInDTO(data.distribution),
        () => {
          setLoading(false)
          setVisible(false);
          onContinue();
        },
        null,
        { onValidationFailed: (errors) => {
            setLoading(false);
            setBeValidationErrors(errors);
        }},
      );
    };

    return (
      <>
        <RingilSidebar
          visible={visible}
          onBack={() => setVisible(false)}
          onContinue={() => form.validateAndSend()}
          heading={tr('RequestCarrierSidebar.requestCarrierHeading', 'Quote carrier')}
          loading={loading}
          dataTestIdContinue={'carrierSelection-save'}
          dataTestIdBack={'carrierSelection-close'}
        >
          <BackendValidationComponent />
          <CarrierDistributionSelector
            allowedDistributionTypes={fromLine ? [DistributionType.CONTRACTED] : [DistributionType.SPOT, DistributionType.FIXED]}
            contractedUnitsByMu={contractedUnitsByMu}
            dataTestIdPrefix={'carrierSelection'}
            pickupTimezoneId={expedition?.locations?.[0]?.timezoneId}
            distribution={form.values.distribution}
            originalDistribution={originalDistribution}
            validationErrors={form.validationErrors
              .filter(e => e.path?.startsWith('distribution'))
              .map(e => ({ path: e.path || '', message: e.message }))}
            onChange={v => form.setFieldValue(form.names.distribution, v)}
            aiCarrierAutoSelectionDto={aiCarrierAutoSelectionDto}
            ladenFactorEligible={ladenFactorEligible}
            defaultLadenFactor={expedition.defaultLadenFactor}
            handlingUnitCount={handlingUnitCount}
            expeditionValidations={[
              {
                locations: expedition?.locations
                  .map(loc => ({
                    id: loc.id,
                    companyLocationId: loc.companyLocationId,
                    city: loc.city,
                    street: loc.streetNr,
                    country: loc.country,
                    postalCode: loc.postalCode,
                    orders: loc.loadedOrders?.map(order => ({
                      items: order.items?.map((item) => ({
                        length: item.length,
                        width: item.width,
                        height: item.height,
                        weight: item.weight,
                        amount: item.amount,
                      })),
                      loadingLocationId: loc.id,
                      unloadingLocationId: expedition?.locations.filter(loc => loc.unloadedOrders?.some(o => o.id === order.id))[0]?.id,
                      totalWeight: order.orderTotalWeight,
                    })),
                    contactEmail: loc.selectedPerson?.contactEmail,
                    contactName: loc.selectedPerson?.contactName,
                    contactPhone: loc.selectedPerson?.contactPhone,
                    deliverySlot: loc.deliverySlot?.dayWithInterval ? {
                      days: loc.deliverySlot.dayWithInterval,
                    } : null,
                  })),
                cargoType: expedition?.cargoTypeCode ? {
                  cargoTypeCode: expedition.cargoTypeCode,
                } : null,
                incotermsValue: expedition?.incoterms?.incotermsValue,
                incotermsDescription: expedition?.incoterms?.incotermsDescription,
                huEkaerNumber: expedition?.huEkaerNumber,
                roUitNumber: expedition?.roUitNumber,
                utnCode: expedition?.utnCode,
                polandSentNumber: expedition?.polandSent?.sentNumber,
                polandSentCarrierUpdateKey: expedition?.polandSent?.sentCarrierUpdateKey,
                attachments: attachments.map(attachment => ({
                  documentType: attachment.documentType,
                  contentType: attachment.contentType,
                })),
              },
            ]}
          />
        </RingilSidebar>
      </>
    )
      ;
  };

  return { RequestCarrierSidebar, setVisible };
};